import {
  Alert,
  Box,
  Chip,
  IconButton,
  Button,
  Divider,
  Skeleton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useMemo, useState } from "react";
import ResourcesProductionInfoDialog from "./ResourcesProductionInfoDialog";
import { getCommodityMeta } from "./functions";
import {
  formatJurisdictionLabel,
  formatNumberWithCommas,
  formatPercent,
} from "../../utils/companiesFormatting";
import AssetResourceDetailDrawer from "./AssetResourceDetailDrawer";
import {
  PRODUCTION_METRIC_CATEGORIES,
  PRODUCTION_METRIC_ORDER,
} from "./productionUtils";
import { TableHeaderRow, TableShell, TableCell } from "../ui/TableShell";

export default function OperationsTab({
  annualData: providedAnnualData,
  annualLoading: providedAnnualLoading,
  annualError: providedAnnualError,
  quarterlyData: providedQuarterlyData,
  quarterlyLoading: providedQuarterlyLoading,
  quarterlyError: providedQuarterlyError,
}) {
  const [periodType, setPeriodType] = useState("annual");
  const [expandedAssets, setExpandedAssets] = useState({});
  const [infoOpen, setInfoOpen] = useState(false);
  const [detailDrawer, setDetailDrawer] = useState({ open: false, type: "", data: null });
  const resolvedAnnualData = providedAnnualData ?? null;
  const resolvedAnnualLoading = providedAnnualLoading ?? false;
  const resolvedAnnualError = providedAnnualError ?? null;
  const resolvedQuarterlyData = providedQuarterlyData ?? null;
  const resolvedQuarterlyLoading = providedQuarterlyLoading ?? false;
  const resolvedQuarterlyError = providedQuarterlyError ?? null;

  const hasAnnualData = Boolean(
    (resolvedAnnualData?.periods && resolvedAnnualData.periods.length) ||
      (resolvedAnnualData?.company_summary?.metrics &&
        resolvedAnnualData.company_summary.metrics.length) ||
      (resolvedAnnualData?.asset_detail && resolvedAnnualData.asset_detail.length)
  );
  const hasQuarterlyData = Boolean(
    (resolvedQuarterlyData?.periods && resolvedQuarterlyData.periods.length) ||
      (resolvedQuarterlyData?.company_summary?.metrics &&
        resolvedQuarterlyData.company_summary.metrics.length) ||
      (resolvedQuarterlyData?.asset_detail && resolvedQuarterlyData.asset_detail.length)
  );
  const showToggle = hasAnnualData && hasQuarterlyData;
  const resolvedPeriodType = showToggle
    ? periodType
    : hasAnnualData
    ? "annual"
    : "quarterly";

  const data = resolvedPeriodType === "quarterly" ? resolvedQuarterlyData : resolvedAnnualData;
  const loading = resolvedPeriodType === "quarterly" ? resolvedQuarterlyLoading : resolvedAnnualLoading;
  const error = resolvedPeriodType === "quarterly" ? resolvedQuarterlyError : resolvedAnnualError;

  const sortPeriods = (items) =>
    [...(items || [])].sort((a, b) => {
      const aTime = a?.end_date ? new Date(a.end_date).getTime() : 0;
      const bTime = b?.end_date ? new Date(b.end_date).getTime() : 0;
      return aTime - bTime;
    });

  const periods = sortPeriods(data?.periods || []);
  const periodKeys = useMemo(() => periods.map((period) => period.key), [periods]);
  const latestPeriod = periods.length ? periods[periods.length - 1] : null;

  const companyMetrics = useMemo(() => data?.company_summary?.metrics || [], [data]);
  const assetDetail = useMemo(() => data?.asset_detail || [], [data]);
  const latestPeriodKey = latestPeriod?.key;
  const producingAssets = useMemo(
    () =>
      assetDetail.filter((asset) => {
        const lifecycle = (asset?.lifecycle || "").toLowerCase();
        return lifecycle === "production";
      }).length,
    [assetDetail]
  );
  const heroCommodityRows = useMemo(() => {
    if (!latestPeriodKey || !companyMetrics.length) return [];
    const allowedCodes = new Set(["ounces_produced", "price_realized", "aisc"]);
    const byCommodity = {};
    companyMetrics.forEach((metric) => {
      const code = metric.metric_code || metric.metric || "";
      const commodity = metric.commodity || metric.commodity_code;
      if (!commodity || !allowedCodes.has(code)) return;
      if (!byCommodity[commodity]) {
        byCommodity[commodity] = { commodity };
      }
      if (!byCommodity[commodity][code]) {
        byCommodity[commodity][code] = [];
      }
      byCommodity[commodity][code].push(metric);
    });
    return Object.values(byCommodity)
      .map((row) => ({
        ...row,
        meta: getCommodityMeta(row.commodity),
      }))
      .sort((a, b) => (a.meta?.label || a.commodity).localeCompare(b.meta?.label || b.commodity));
  }, [companyMetrics, latestPeriodKey]);
  const allExpanded = useMemo(() => {
    if (!assetDetail.length) return false;
    return assetDetail.every((asset) => expandedAssets[asset.asset_id]);
  }, [assetDetail, expandedAssets]);

  const groupMetricsByCategory = (metrics) => {
    const grouped = metrics.reduce((acc, metric) => {
      const code = metric.metric_code || metric.metric || "";
      const category = PRODUCTION_METRIC_CATEGORIES[code] || "Other";
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push({ ...metric, category });
      return acc;
    }, {});

    return Object.keys(grouped)
      .sort((a, b) => {
        const aOrder = PRODUCTION_METRIC_ORDER[a] ?? Number.MAX_SAFE_INTEGER;
        const bOrder = PRODUCTION_METRIC_ORDER[b] ?? Number.MAX_SAFE_INTEGER;
        if (aOrder !== bOrder) return aOrder - bOrder;
        return a.localeCompare(b);
      })
      .map((category) => ({
        category,
        metrics: grouped[category]
          .slice()
          .sort((a, b) => {
            const metricLabelA = (a.label || a.metric || a.metric_code || "").toString();
            const metricLabelB = (b.label || b.metric || b.metric_code || "").toString();
            if (metricLabelA !== metricLabelB) {
              return metricLabelA.localeCompare(metricLabelB);
            }
            const commodityA =
              getCommodityMeta(a.commodity || a.commodity_code)?.label ||
              a.commodity ||
              a.commodity_code ||
              "";
            const commodityB =
              getCommodityMeta(b.commodity || b.commodity_code)?.label ||
              b.commodity ||
              b.commodity_code ||
              "";
            return commodityA.localeCompare(commodityB);
          }),
      }));
  };
  const buildMetricRows = (metrics = []) => {
    const grouped = metrics.reduce((acc, metric) => {
      const code = metric.metric_code || metric.metric || "";
      const category = PRODUCTION_METRIC_CATEGORIES[code] || "Other";
      if (!acc[category]) acc[category] = [];
      acc[category].push(metric);
      return acc;
    }, {});

    return Object.keys(grouped)
      .sort((a, b) => {
        const aOrder = PRODUCTION_METRIC_ORDER[a] ?? Number.MAX_SAFE_INTEGER;
        const bOrder = PRODUCTION_METRIC_ORDER[b] ?? Number.MAX_SAFE_INTEGER;
        if (aOrder !== bOrder) return aOrder - bOrder;
        return a.localeCompare(b);
      })
      .flatMap((category) => {
        const rows = grouped[category].map((metric) => {
          const unitLabel = metric.unit_display || metric.unit;
          const baseLabel = metric.label || metric.metric || metric.metric_code || "Metric";
          const label = unitLabel ? `${baseLabel} (${unitLabel})` : baseLabel;
          const code = metric.metric_code || metric.metric || "";
          return {
            type: "metric",
            key: metric.metric_key || `${code}-${metric.commodity || "company"}-${label}`,
            label,
            code,
            commodity: metric.commodity,
            values: metric.values || {},
          };
        });
        rows.sort((a, b) => {
          if (a.label !== b.label) return a.label.localeCompare(b.label);
          const commodityA = a.commodity || "";
          const commodityB = b.commodity || "";
          return commodityA.localeCompare(commodityB);
        });
        return [
          { type: "category", key: `category-${category}`, label: category },
          ...rows,
        ];
      });
  };
  const openDetailDrawer = (type, data) => {
    setDetailDrawer({ open: true, type, data });
  };
  const closeDetailDrawer = () => {
    setDetailDrawer({ open: false, type: "", data: null });
  };
  const toggleAssetExpanded = (assetId) => {
    setExpandedAssets((prev) => ({ ...prev, [assetId]: !prev[assetId] }));
  };
  const expandAllAssets = () => {
    const next = {};
    assetDetail.forEach((asset) => {
      if (asset?.asset_id != null) {
        next[asset.asset_id] = true;
      }
    });
    setExpandedAssets(next);
  };
  const collapseAllAssets = () => {
    setExpandedAssets({});
  };


  const renderMetricsTable = (metrics, tablePeriods, tablePeriodKeys) => (
    <TableShell sx={{ overflowX: { xs: "scroll", md: "hidden" }, WebkitOverflowScrolling: "touch" }}>
      <TableHeaderRow
        gridTemplateColumns={`2.2fr repeat(${tablePeriods.length}, 1fr)`}
      >
        <TableCell label="" />
        {tablePeriods.map((period) => (
          <TableCell key={period.key} label={period.label} />
        ))}
      </TableHeaderRow>
      <Stack spacing={0} sx={{ px: 1, py: 0.5 }}>
        {groupMetricsByCategory(metrics).map((group) => (
          <Box key={group.category}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: `2.2fr repeat(${tablePeriods.length}, 1fr)`,
                gap: 1,
                alignItems: "center",
                py: 0.4,
                backgroundColor: "rgba(34, 35, 37, 0.02)",
                borderRadius: 0.75,
              }}
            >
              <Typography
                variant="caption"
                fontWeight={700}
                color="text.secondary"
                sx={{ gridColumn: "1 / -1" }}
              >
                {group.category}
              </Typography>
            </Box>
            {group.metrics.map((metric) => {
              const unitLabel = metric.unit_display || metric.unit;
              const metricLabel = unitLabel ? `${metric.label} (${unitLabel})` : metric.label;
              const isAisc = (metric.metric_code || metric.metric || "") === "aisc";
              return (
                <Box
                  key={metric.metric_key}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: `2.2fr repeat(${tablePeriods.length}, 1fr)`,
                    gap: 1,
                    alignItems: "center",
                    py: 0.5,
                  }}
                >
                  <Typography variant="caption" fontWeight={600} sx={{ pl: 2 }}>
                    {metricLabel}
                  </Typography>
                  {tablePeriodKeys.map((key) => (
                    <Typography
                      key={`${metric.metric_key}-${key}`}
                      variant="caption"
                      color="text.secondary"
                    >
                      {metric.values?.[key] !== undefined
                        ? formatNumberWithCommas(metric.values[key], isAisc ? 0 : null)
                        : "—"}
                    </Typography>
                  ))}
                </Box>
              );
            })}
          </Box>
        ))}
      </Stack>
    </TableShell>
  );

  const formatMetricValue = (metric, key) => {
    const value = metric.values?.[key];
    if (value === undefined || value === null) return "—";
    const isAisc = metric.code === "aisc";
    return formatNumberWithCommas(value, isAisc ? 0 : null);
  };

  const hasOperationalData = companyMetrics.length > 0 || assetDetail.length > 0;

  return (
    <Box sx={{ p: { xs: 1, md: 2 }, height: "100%" }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={{ xs: 0.5, sm: 1 }}
        alignItems={{ xs: "flex-start", sm: "center" }}
        sx={{ pb: { xs: 0.5, sm: 1 } }}
      >
        <Typography variant="h6" fontWeight={700}>
          Operations
        </Typography>
        {!loading && !error && hasOperationalData && (
          <IconButton
            size="small"
            onClick={() => setInfoOpen(true)}
            aria-label="Operations information"
          >
            <InfoOutlinedIcon fontSize="small" />
          </IconButton>
        )}
      </Stack>
      <ResourcesProductionInfoDialog open={infoOpen} onClose={() => setInfoOpen(false)} />
      {loading ? (
        <Box sx={{ mt: 1, mb: 2 }}>
          <Skeleton height={96} />
        </Box>
      ) : error ? (
        <Alert severity="error">Unable to load operations data.</Alert>
      ) : !hasOperationalData ? (
        <Stack spacing={1} sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary">
            No published operational information available.
          </Typography>
          <Divider sx={{ pt: 2, mb: 2 }} />
          <Typography variant="body2" color="text.secondary" sx={{ pt: 2 }}>
            <b>ResourceNAV</b> is currently working with companies to build out this asset database to
            create a rich dataset of resources, production and forecast metrics to use for enhanced display & screening
            purposes.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            If you are a mining company, claim your company profile and enter in asset
            information to publish data on the platform.
          </Typography>
        </Stack>
      ) : (
        <Stack spacing={3}>
          {hasOperationalData && (
            <Box
              sx={{
                border: "1px solid rgba(0,0,0,0.08)",
                borderRadius: 1.5,
                backgroundColor: "rgba(34, 35, 37, 0.02)",
                boxShadow: "0 1px 6px rgba(15, 23, 42, 0.06)",
                px: 2.5,
                py: 2,
              }}
            >
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "2fr 1fr" },
                  gap: 2,
                  alignItems: "center",
                }}
              >
                <Box sx={{ pr: { md: 2 } }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={700}>
                    Production snapshot
                  </Typography>
                  <Stack spacing={1} sx={{ mt: 1 }}>
                    {heroCommodityRows.length ? (
                      heroCommodityRows.map((row) => {
                        const meta = row.meta;
                        const chipLabel = meta?.symbol || row.commodity;
                        const label = meta?.label || row.commodity;
                        const ounces = row.ounces_produced || [];
                        const price = row.price_realized || [];
                        const aisc = row.aisc || [];
                        const formatMetricList = (metricsList) => {
                          if (!metricsList.length) return "—";
                          const byUnit = new Map();
                          metricsList.forEach((metric) => {
                            const unit = metric.unit_display || metric.unit || "";
                            const currency = metric.currency || "";
                            const key = currency ? `${currency}|${unit}` : unit;
                            if (!byUnit.has(key)) {
                              byUnit.set(key, metric);
                            }
                          });
                          const formatted = [];
                          byUnit.forEach((metric, unitKey) => {
                            const value = metric.values?.[latestPeriodKey];
                            if (value === undefined || value === null) return;
                            const isAiscMetric = (metric.metric_code || metric.metric || "") === "aisc";
                            const formattedValue = formatNumberWithCommas(value, isAiscMetric ? 0 : null);
                            const unit = unitKey.includes("|") ? unitKey.split("|")[1] : unitKey;
                            const currency = metric.currency || "";
                            const resolvedUnit =
                              currency && unit && !unit.includes(currency) ? `${currency}${unit}` : unit;
                            formatted.push(
                              `${formattedValue}${resolvedUnit ? ` ${resolvedUnit}` : ""}`
                            );
                          });
                          return formatted.length ? formatted.join(" · ") : "—";
                        };
                        return (
                          <Stack key={row.commodity} spacing={0.75}>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Chip
                                label={chipLabel}
                                size="small"
                                sx={{
                                  backgroundColor: meta?.color || "action.hover",
                                  color: "#0f172a",
                                  fontWeight: 700,
                                  height: 24,
                                }}
                                title={label}
                              />
                              <Typography variant="subtitle2" fontWeight={700}>
                                {label}
                              </Typography>
                            </Stack>
                            <Stack
                              direction={{ xs: "column", sm: "row" }}
                              spacing={1.5}
                              sx={{ pl: 0.25 }}
                            >
                              <Typography variant="caption" color="text.secondary">
                                Oz produced:{" "}
                                <Box component="span" sx={{ color: "text.primary", fontWeight: 600 }}>
                                  {formatMetricList(ounces)}
                                </Box>
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Price realized:{" "}
                                <Box component="span" sx={{ color: "text.primary", fontWeight: 600 }}>
                                  {formatMetricList(price)}
                                </Box>
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                AISC:{" "}
                                <Box component="span" sx={{ color: "text.primary", fontWeight: 600 }}>
                                  {formatMetricList(aisc)}
                                </Box>
                              </Typography>
                            </Stack>
                          </Stack>
                        );
                      })
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        No published production metrics.
                      </Typography>
                    )}
                  </Stack>
                </Box>
                <Box
                  sx={{
                    borderLeft: { md: "1px solid rgba(0,0,0,0.08)" },
                    pl: { md: 2 },
                    py: { xs: 1, md: 0 },
                  }}
                >
                  <Typography variant="caption" color="text.secondary" fontWeight={700}>
                    Producing assets
                  </Typography>
                  <Typography variant="h6" fontWeight={700}>
                    {producingAssets}
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}
          <Box>
            <Typography variant="h6" fontWeight={700} gutterBottom>
              Company Production Summary
            </Typography>
            {showToggle && (
              <ToggleButtonGroup
                value={periodType}
                exclusive
                size="small"
                onChange={(_, value) => value && setPeriodType(value)}
              >
                <ToggleButton value="annual">Annual</ToggleButton>
                <ToggleButton value="quarterly">Quarterly</ToggleButton>
              </ToggleButtonGroup>
            )}
            {companyMetrics.length ? (
              <Box sx={{ mt: 1 }}>
                {renderMetricsTable(companyMetrics, periods, periodKeys)}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                No published production metrics available.
              </Typography>
            )}
          </Box>

          <Stack spacing={1.5}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography variant="h6" fontWeight={700}>
                Company Asset Detail
              </Typography>
              <Button
                size="small"
                variant="text"
                onClick={allExpanded ? collapseAllAssets : expandAllAssets}
                sx={{ fontWeight: 600 }}
              >
                {allExpanded ? "Collapse all" : "Expand all"}
              </Button>
            </Stack>
            {assetDetail.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No published asset-level operational data.
              </Typography>
            ) : (
              <Box
                sx={{
                  border: "1px solid rgba(0,0,0,0.08)",
                  borderRadius: 1.5,
                  overflowX: { xs: "scroll", md: "auto" },
                  WebkitOverflowScrolling: "touch",
                }}
              >
                <Box
                  component="table"
                  sx={{
                    width: "100%",
                    borderCollapse: "collapse"
                  }}
                >
                  <Box component="thead">
                    <Box component="tr" sx={{ backgroundColor: "rgba(34, 35, 37, 0.04)" }}>
                        <Box
                          component="th"
                          sx={{
                            px: 0.5,
                            py: 0.75,
                            textAlign: "left",
                            width: { xs: 125, sm: 300 },
                            minWidth: { xs: 125, sm: 300 },
                            maxWidth: { xs: 125, sm: 300 },
                            borderRight: "1px solid rgba(0,0,0,0.08)",
                          }}
                        />
                      <Box
                        component="th"
                        rowSpan={2}
                        sx={{
                          px: 0.5,
                          py: 0.75,
                          textAlign: "left",
                          borderRight: "1px solid rgba(0,0,0,0.08)",
                        }}
                      >
                        <Typography variant="caption" fontWeight={700}>
                          Metric
                        </Typography>
                      </Box>
                      {periods.map((period) => (
                        <Box
                          key={period.key}
                          component="th"
                          rowSpan={2}
                          sx={{ px: 0.5, py: 0.75, textAlign: "center" }}
                        >
                          <Typography variant="caption" fontWeight={700}>
                            {period.label}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                  <Box component="tbody">
                    {assetDetail.flatMap((asset) => {
                      const assetId = asset.asset_id;
                      const isExpanded = Boolean(expandedAssets[assetId]);
                      const assetRows = buildMetricRows(asset.metrics || []);
                      const assetLabel = (
                        <Stack direction="column" spacing={0.25} alignItems="flex-start">
                          <Stack direction="row" spacing={0} alignItems="center">
                            <IconButton
                              size="small"
                              onClick={() => toggleAssetExpanded(assetId)}
                              sx={{ mt: 0, width: 24, height: 24 }}
                              aria-label="Toggle asset resources"
                            >
                              <Box
                                component="span"
                                sx={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  width: 20,
                                  height: 20,
                                  fontSize: "0.75rem",
                                  lineHeight: 1,
                                  color: "text.secondary",
                                }}
                              >
                                {isExpanded ? "▼" : "▶"}
                              </Box>
                            </IconButton>
                            <Tooltip title={asset.asset_name} placement="top" arrow>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 700,
                                  fontSize: "0.75rem",
                                  cursor: "pointer",
                                  textDecoration: "none",
                                  "&:hover": { textDecoration: "underline" },
                                  maxWidth: { xs: 125, sm: 220 },
                                  whiteSpace: { xs: "normal", sm: "nowrap" },
                                  overflow: { xs: "visible", sm: "hidden" },
                                  textOverflow: { xs: "clip", sm: "ellipsis" },
                                  display: "block",
                                }}
                                onClick={() => openDetailDrawer("asset", asset)}
                              >
                                {asset.asset_name}
                              </Typography>
                            </Tooltip>
                          </Stack>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.7rem", pl: 3 }}>
                            {[
                              formatJurisdictionLabel(asset.jurisdiction),
                              asset.lifecycle || asset.lifecycle_stage,
                              formatPercent(asset.ownership_percentage, 2) || "—",
                            ]
                              .filter(Boolean)
                              .join(" · ") || "—"}
                          </Typography>
                        </Stack>
                      );

                      const assetRowsRendered = (assetRows.length ? assetRows : [null]).map(
                        (metric, index) => (
                          <Box
                            component="tr"
                            key={`asset-${assetId}-${index}`}
                            sx={{
                              borderTop: "1px solid rgba(0,0,0,0.06)",
                              backgroundColor: "rgba(34, 35, 37, 0.02)",
                            }}
                          >
                            <Box
                              component="td"
                              sx={{
                                px: 0.5,
                                py: 0.5,
                                verticalAlign: "top",
                                width: { xs: 125, sm: 300 },
                                minWidth: { xs: 125, sm: 300 },
                                maxWidth: { xs: 125, sm: 300 },
                                borderRight: "1px solid rgba(0,0,0,0.08)",
                              }}
                            >
                              {index === 0 ? assetLabel : null}
                            </Box>
                            <Box component="td" sx={{ px: 0.5, py: 0.5, borderRight: "1px solid rgba(0,0,0,0.08)" }}>
                              {metric ? (
                                metric.type === "category" ? (
                                  <Typography variant="caption" fontWeight={700} color="text.secondary">
                                    {metric.label}
                                  </Typography>
                                ) : (
                                  <Typography variant="caption" fontWeight={600}>{metric.label}</Typography>
                                )
                              ) : (
                                <Typography variant="body2" color="text.disabled" sx={{ fontSize: "0.75rem" }}>
                                  —
                                </Typography>
                              )}
                            </Box>
                            {periodKeys.map((key) => (
                              <Box key={`${assetId}-${index}-${key}`} component="td" sx={{ px: 0.5, py: 0.5, textAlign: "center" }}>
                                <Typography variant="body2" sx={{ fontSize: "0.75rem", color: metric ? "text.primary" : "text.disabled" }}>
                                  {metric ? (metric.type === "category" ? "" : formatMetricValue(metric, key)) : "—"}
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        )
                      );

                      if (!isExpanded) {
                        return assetRowsRendered;
                      }

                      const resourceRowsRendered = (asset.resource_detail || []).flatMap((resource) => {
                        const resourceRows = buildMetricRows(resource.metrics || []);
                        const resourceLabel = (
                          <Stack direction="column" spacing={0.25} alignItems="flex-start">
                            <Tooltip title={resource.resource_reserve_name || "Resource reserve"} placement="top" arrow>
                              <Typography
                                variant="body2"
                                fontWeight={600}
                                color="text.secondary"
                                sx={{
                                  fontSize: "0.75rem",
                                  cursor: "pointer",
                                  textDecoration: "none",
                                  "&:hover": { textDecoration: "underline" },
                                  maxWidth: { xs: 125, sm: 220 },
                                  whiteSpace: { xs: "normal", sm: "nowrap" },
                                  overflow: { xs: "visible", sm: "hidden" },
                                  textOverflow: { xs: "clip", sm: "ellipsis" },
                                  display: "block",
                                  pl: 4,
                                }}
                                onClick={() => openDetailDrawer("resource", resource)}
                              >
                                {resource.resource_reserve_name || "Resource reserve"}
                              </Typography>
                            </Tooltip>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ fontSize: "0.7rem", pl: 4 }}
                            >
                              {resource.lifecycle || resource.lifecycle_stage || "—"}
                            </Typography>
                          </Stack>
                        );
                        return (resourceRows.length ? resourceRows : [null]).map((metric, index) => (
                          <Box
                            component="tr"
                            key={`resource-${assetId}-${resource.resource_reserve_id}-${index}`}
                            sx={{
                              borderTop: "1px solid rgba(0,0,0,0.06)",
                              backgroundColor: "transparent",
                            }}
                          >
                            <Box
                              component="td"
                              sx={{
                                px: 0.5,
                                py: 0.5,
                                verticalAlign: "top",
                                width: { xs: 125, sm: 300 },
                                minWidth: { xs: 125, sm: 300 },
                                maxWidth: { xs: 125, sm: 300 },
                                borderRight: "1px solid rgba(0,0,0,0.08)",
                              }}
                            >
                              {index === 0 ? resourceLabel : null}
                            </Box>
                            <Box component="td" sx={{ px: 0.5, py: 0.5, borderRight: "1px solid rgba(0,0,0,0.08)" }}>
                              {metric ? (
                                metric.type === "category" ? (
                                  <Typography variant="caption" fontWeight={700} color="text.secondary">
                                    {metric.label}
                                  </Typography>
                                ) : (
                                  <Typography variant="caption" fontWeight={600}>{metric.label}</Typography>
                                )
                              ) : (
                                <Typography variant="body2" color="text.disabled" sx={{ fontSize: "0.75rem" }}>
                                  —
                                </Typography>
                              )}
                            </Box>
                            {periodKeys.map((key) => (
                              <Box key={`${assetId}-${resource.resource_reserve_id}-${index}-${key}`} component="td" sx={{ px: 0.5, py: 0.5, textAlign: "center" }}>
                                <Typography variant="body2" sx={{ fontSize: "0.75rem", color: metric ? "text.primary" : "text.disabled" }}>
                                  {metric ? (metric.type === "category" ? "" : formatMetricValue(metric, key)) : "—"}
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        ));
                      });

                      return [...assetRowsRendered, ...resourceRowsRendered];
                    })}
                  </Box>
                </Box>
              </Box>
            )}
          </Stack>
        </Stack>
      )}
      <AssetResourceDetailDrawer
        open={detailDrawer.open}
        onClose={closeDetailDrawer}
        type={detailDrawer.type}
        data={detailDrawer.data}
        showResourceReportInfo={false}
      />
    </Box>
  );
}
